import { create } from 'zustand';

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5188/api/v1";

export interface TourSpeciesDto {
    speciesId: number;
    scientificName: string;
    commonName: string;
    photoUrl: string | null;
    themeColor: string | null;
}

export interface TourTargetLocationDto {
    locationId: string;
    locationName: string;
    latitude: number;
    longitude: number;
    recentObservationDate: string;
    totalHowMany: number;
    distanceKm: number;
    matchingSpecies: TourSpeciesDto[];
}

export interface SavedTargetDto {
    id: number;
    speciesId: number | null;
    speciesName: string | null;
    searchTerm: string | null;
    label: string;
    createdAt: string;
}

interface TourStore {
    appMode: "radar" | "tour";
    setAppMode: (mode: "radar" | "tour") => void;

    // Search Params
    tourRadiusKm: number;
    tourDaysAgo: number;
    tourSearchTerm: string;

    setTourParams: (radiusKm: number, daysAgo: number, searchTerm: string) => void;

    tourLocations: TourTargetLocationDto[];
    isTourLoading: boolean;
    fetchTourLocations: (lat: number, lng: number) => Promise<void>;

    savedTargets: SavedTargetDto[];
    fetchSavedTargets: (token: string) => Promise<void>;
    saveTarget: (token: string, label: string) => Promise<void>;
    deleteTarget: (token: string, id: number) => Promise<void>;

    selectedTourLocationId: string | null;
    setSelectedTourLocationId: (id: string | null) => void;
}

export const useTourStore = create<TourStore>((set, get) => ({
    appMode: "radar",
    setAppMode: (mode) => set({ appMode: mode }),

    selectedTourLocationId: null,
    setSelectedTourLocationId: (id) => set({ selectedTourLocationId: id }),

    tourRadiusKm: 50.0,
    tourDaysAgo: 30,
    tourSearchTerm: "",

    setTourParams: (radiusKm, daysAgo, searchTerm) => {
        set({ tourRadiusKm: radiusKm, tourDaysAgo: daysAgo, tourSearchTerm: searchTerm });
    },

    tourLocations: [],
    isTourLoading: false,

    fetchTourLocations: async (lat: number, lng: number) => {
        const { tourRadiusKm, tourDaysAgo, tourSearchTerm } = get();
        
        if (!tourSearchTerm.trim()) {
            set({ tourLocations: [] });
            return;
        }

        set({ isTourLoading: true });

        try {
            const queryParams = new URLSearchParams({
                lat: lat.toString(),
                lng: lng.toString(),
                radiusKm: tourRadiusKm.toString(),
                daysAgo: tourDaysAgo.toString(),
                searchTerm: tourSearchTerm
            });

            const response = await fetch(`${API_BASE_URL}/tour/target?${queryParams.toString()}`);
            if (response.ok) {
                const data = await response.json();
                set({ tourLocations: data });
            } else {
                set({ tourLocations: [] });
            }
        } catch (error) {
            console.error("Error fetching tour locations:", error);
            set({ tourLocations: [] });
        } finally {
            set({ isTourLoading: false });
        }
    },

    savedTargets: [],

    fetchSavedTargets: async (token: string) => {
        if (!token) return;
        try {
            const res = await fetch(`${API_BASE_URL}/tour/saved`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                set({ savedTargets: data });
            }
        } catch (error) {
            console.error("Failed to fetch saved tour targets", error);
        }
    },

    saveTarget: async (token: string, label: string) => {
        const { tourSearchTerm } = get();
        if (!token) return;
        
        try {
            const res = await fetch(`${API_BASE_URL}/tour/saved`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    searchTerm: tourSearchTerm,
                    label
                })
            });
            if (res.ok) {
                get().fetchSavedTargets(token);
            }
        } catch (error) {
            console.error("Failed to save tour target", error);
        }
    },

    deleteTarget: async (token: string, id: number) => {
        if (!token) return;
        try {
            const res = await fetch(`${API_BASE_URL}/tour/saved/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                set((state) => ({ savedTargets: state.savedTargets.filter(t => t.id !== id) }));
            }
        } catch (error) {
            console.error("Failed to delete tour target", error);
        }
    }
}));
